import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dumbbell, CheckCircle, XCircle, Clock, Eye, FileText,
  Loader2, AlertTriangle, ChevronRight, Award, Globe, AlertCircle
} from "lucide-react";

const statusColors = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  incomplete: "bg-slate-100 text-slate-600",
  more_info_requested: "bg-orange-100 text-orange-800",
};

const resolveUploadUrl = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname.includes("/api/uploads/")) {
      return `${window.location.origin}${parsed.pathname}`;
    }
    return url;
  } catch {
    return url;
  }
};

const isPreviewableImage = (url) => /\.(jpg|jpeg|png|webp|heic)$/i.test(url || "");

export function SPVerificationTab({ onCountUpdate }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [reviewType, setReviewType] = useState("identity");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/kyc/all?persona=coach&status=${filter}`);
      setSubmissions(res.data.submissions || []);
      if (onCountUpdate) onCountUpdate(res.data.count || 0);
    } catch {
      toast.error("Failed to load SP verification data");
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filter]);

  useEffect(() => {
    if (selectedUser) {
      if (selectedUser.identity_review_status === "approved") {
        setReviewType("credentials");
      } else {
        setReviewType("identity");
      }
    }
  }, [selectedUser]);

  const handleReview = async (action) => {
    if (!selectedUser) return;
    setProcessing(true);
    try {
      await api.post("/admin/kyc/review", {
        user_id: selectedUser.user_id,
        persona: "coach",
        review_type: reviewType,
        action,
        notes: reviewNotes,
      });
      toast.success(`${reviewType === "credentials" ? "Credentials" : "Identity"} ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "info requested"}`);
      setSelectedUser(null);
      setReviewNotes("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    }
    setProcessing(false);
  };

  const resolvedGovIdUrl = selectedUser ? resolveUploadUrl(selectedUser.gov_id_url) : "";
  const resolvedSelfieUrl = selectedUser ? resolveUploadUrl(selectedUser.selfie_url) : "";

  return (
    <div className="space-y-4" data-testid="sp-verification-tab">
      {/* Filter row */}
      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <Button key={f} variant={filter === f ? "default" : "outline"} size="sm" onClick={() => setFilter(f)} className="capitalize" data-testid={`sp-filter-${f}`}>
            {f}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Dumbbell className="w-4 h-4" />
              SP Submissions ({submissions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Dumbbell className="w-10 h-10 mx-auto mb-2 opacity-20" />
                <p className="text-sm">No {filter} SP submissions</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[520px] overflow-y-auto">
                {submissions.map((sub) => {
                  const effectiveStatus = sub.identity_review_status === "approved"
                    ? (sub.credentials_review_status || "incomplete")
                    : (sub.identity_review_status || "incomplete");
                  const reviewStage = sub.identity_review_status === "approved" ? "Level 2" : "Level 1";
                  const isSelected = selectedUser?.user_id === sub.user_id;
                  return (
                    <div
                      key={sub.user_id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${isSelected ? "border-primary bg-primary/5" : "hover:bg-slate-50"}`}
                      onClick={() => setSelectedUser(sub)}
                      data-testid={`sp-submission-${sub.user_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-purple-50 flex items-center justify-center">
                            <Dumbbell className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{sub.user_name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground">{sub.user_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[9px]">{reviewStage}</Badge>
                          <Badge className={statusColors[effectiveStatus] || statusColors.incomplete}>{effectiveStatus}</Badge>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                      {sub.gov_id_type && (
                        <p className="text-xs text-muted-foreground mt-1.5 ml-12">
                          ID: {sub.gov_id_type}
                          {sub.licenses?.length > 0 ? ` | ${sub.licenses.length} license(s)` : ""}
                          {sub.associations?.length > 0 ? ` | ${sub.associations.length} assoc.` : ""}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail / Review Panel */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" />Review Details</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedUser ? (
              <div className="space-y-4 max-h-[520px] overflow-y-auto">
                {/* User Info */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-sm">{selectedUser.user_name}</h4>
                  <p className="text-xs text-muted-foreground">{selectedUser.user_email}</p>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    <div><span className="text-muted-foreground">Identity:</span> <Badge className={`ml-1 text-[9px] ${statusColors[selectedUser.identity_review_status] || statusColors.incomplete}`}>{selectedUser.identity_review_status || "incomplete"}</Badge></div>
                    <div><span className="text-muted-foreground">Credentials:</span> <Badge className={`ml-1 text-[9px] ${statusColors[selectedUser.credentials_review_status] || statusColors.incomplete}`}>{selectedUser.credentials_review_status || "incomplete"}</Badge></div>
                    <div><span className="text-muted-foreground">ID Type:</span> <span className="font-medium">{selectedUser.gov_id_type || "N/A"}</span></div>
                    <div><span className="text-muted-foreground">KYC Level:</span> <span className="font-medium">{selectedUser.kyc_level || 1}</span></div>
                  </div>
                </div>

                {/* Identity Documents */}
                <div className="space-y-2">
                  <h5 className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Identity Documents</h5>
                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Government ID</span>
                      {resolvedGovIdUrl ? (
                        <Button size="sm" variant="outline" onClick={() => window.open(resolvedGovIdUrl, "_blank")} data-testid="sp-view-gov-id"><Eye className="w-3 h-3 mr-1" />View</Button>
                      ) : (<Badge variant="outline" className="text-[10px]">Not uploaded</Badge>)}
                    </div>
                    {resolvedGovIdUrl && isPreviewableImage(resolvedGovIdUrl) && (
                      <img src={resolvedGovIdUrl} alt="Gov ID" className="mt-2 max-h-32 rounded border object-contain" onError={(e) => (e.target.style.display = "none")} />
                    )}
                  </div>
                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Selfie</span>
                      {resolvedSelfieUrl ? (
                        <Button size="sm" variant="outline" onClick={() => window.open(resolvedSelfieUrl, "_blank")} data-testid="sp-view-selfie"><Eye className="w-3 h-3 mr-1" />View</Button>
                      ) : (<Badge variant="outline" className="text-[10px]">Not uploaded</Badge>)}
                    </div>
                    {resolvedSelfieUrl && isPreviewableImage(resolvedSelfieUrl) && (
                      <img src={resolvedSelfieUrl} alt="Selfie" className="mt-2 max-h-32 rounded border object-contain" onError={(e) => (e.target.style.display = "none")} />
                    )}
                  </div>
                </div>

                {/* Professional Credentials (Licenses & Associations) */}
                {(selectedUser.licenses?.length > 0 || selectedUser.associations?.length > 0) && (
                  <div className="border rounded-lg p-3 bg-purple-50/50 space-y-3" data-testid="sp-credentials-review">
                    <h5 className="text-xs font-heading uppercase tracking-wider text-purple-700 flex items-center gap-1">
                      <Award className="w-3 h-3" />Professional Credentials (Level 2)
                    </h5>

                    {selectedUser.licenses?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium flex items-center gap-1 text-purple-700"><FileText className="w-3 h-3" />Licenses ({selectedUser.licenses.length})</p>
                        {selectedUser.licenses.map((lic, idx) => (
                          <div key={idx} className="bg-white border rounded-lg p-2.5 space-y-1" data-testid={`sp-review-license-${idx}`}>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                              <p><span className="text-muted-foreground">Name:</span> <span className="font-medium">{lic.name}</span></p>
                              <p><span className="text-muted-foreground">Issuing Body:</span> <span className="font-medium">{lic.issuing_body}</span></p>
                              <p><span className="text-muted-foreground">License #:</span> <span className="font-medium">{lic.license_number || "—"}</span></p>
                              <p><span className="text-muted-foreground">Expiry:</span> <span className="font-medium">{lic.expiry_date || "—"}</span></p>
                            </div>
                            {lic.proof_data && (
                              <div className="mt-1.5 pt-1.5 border-t">
                                {lic.proof_data.startsWith("data:image") ? (
                                  <img src={lic.proof_data} alt={`License ${idx + 1}`} className="max-h-20 rounded border object-contain cursor-pointer"
                                    onClick={() => { const w = window.open("", "_blank"); w.document.write(`<img src="${lic.proof_data}" style="max-width:100%" />`); }} />
                                ) : (
                                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { const w = window.open("", "_blank"); w.document.write(`<iframe src="${lic.proof_data}" width="100%" height="100%" />`); }}>
                                    <Eye className="w-3 h-3 mr-1" />View Proof
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedUser.associations?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium flex items-center gap-1 text-purple-700"><Globe className="w-3 h-3" />Associations ({selectedUser.associations.length})</p>
                        {selectedUser.associations.map((assoc, idx) => (
                          <div key={idx} className="bg-white border rounded-lg p-2.5" data-testid={`sp-review-assoc-${idx}`}>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                              <p><span className="text-muted-foreground">Name:</span> <span className="font-medium">{assoc.name}</span></p>
                              <p><span className="text-muted-foreground">Level:</span> <Badge className="text-[9px] bg-purple-100 text-purple-800">{assoc.membership_level}</Badge></p>
                              <p><span className="text-muted-foreground">ID:</span> <span className="font-medium">{assoc.membership_id || "—"}</span></p>
                            </div>
                            {assoc.proof_data && (
                              <div className="mt-1.5 pt-1.5 border-t">
                                {assoc.proof_data.startsWith("data:image") ? (
                                  <img src={assoc.proof_data} alt={`Assoc ${idx + 1}`} className="max-h-20 rounded border object-contain cursor-pointer"
                                    onClick={() => { const w = window.open("", "_blank"); w.document.write(`<img src="${assoc.proof_data}" style="max-width:100%" />`); }} />
                                ) : (
                                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { const w = window.open("", "_blank"); w.document.write(`<iframe src="${assoc.proof_data}" width="100%" height="100%" />`); }}>
                                    <Eye className="w-3 h-3 mr-1" />View Proof
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Review Actions */}
                {((selectedUser.identity_review_status === "pending") ||
                  (selectedUser.identity_review_status === "approved" && selectedUser.credentials_review_status === "pending")) && (
                  <div className="space-y-3 pt-3 border-t">
                    {/* Review Type Toggle */}
                    {selectedUser.identity_review_status === "approved" && selectedUser.credentials_review_status === "pending" && (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled className="flex-1 text-xs">Identity (done)</Button>
                        <Button variant={reviewType === "credentials" ? "default" : "outline"} size="sm" onClick={() => setReviewType("credentials")} className="flex-1 text-xs">Credentials</Button>
                      </div>
                    )}
                    {selectedUser.identity_review_status === "pending" && (
                      <div className="flex gap-2">
                        <Button variant={reviewType === "identity" ? "default" : "outline"} size="sm" onClick={() => setReviewType("identity")} className="flex-1 text-xs">Identity</Button>
                        <Button variant={reviewType === "credentials" ? "default" : "outline"} size="sm" onClick={() => setReviewType("credentials")} disabled={selectedUser.identity_review_status !== "approved"} className="flex-1 text-xs">Credentials</Button>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">Reviewing: <span className="font-medium capitalize">{reviewType}</span></p>
                    <Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Review notes (optional)..." rows={2} className="text-sm" />
                    <div className="flex gap-2">
                      <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => handleReview("approve")} disabled={processing} data-testid="sp-approve-btn">
                        {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" />Approve</>}
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => handleReview("request_more_info")} disabled={processing} data-testid="sp-more-info-btn">
                        {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><AlertTriangle className="w-3 h-3 mr-1" />More Info</>}
                      </Button>
                      <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleReview("reject")} disabled={processing} data-testid="sp-reject-btn">
                        {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><XCircle className="w-3 h-3 mr-1" />Reject</>}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Previous Review Notes */}
                {selectedUser.identity_review_notes && (
                  <div className="p-2.5 bg-slate-50 rounded-lg text-xs">
                    <p className="font-medium text-muted-foreground mb-1">Identity Review Notes:</p>
                    <p>{selectedUser.identity_review_notes}</p>
                    {selectedUser.identity_reviewed_at && (
                      <p className="text-muted-foreground mt-1">Reviewed: {new Date(selectedUser.identity_reviewed_at).toLocaleDateString()}</p>
                    )}
                  </div>
                )}
                {selectedUser.credentials_review_notes && (
                  <div className="p-2.5 bg-purple-50 rounded-lg text-xs">
                    <p className="font-medium text-purple-700 mb-1">Credentials Review Notes:</p>
                    <p>{selectedUser.credentials_review_notes}</p>
                    {selectedUser.credentials_reviewed_at && (
                      <p className="text-muted-foreground mt-1">Reviewed: {new Date(selectedUser.credentials_reviewed_at).toLocaleDateString()}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Dumbbell className="w-10 h-10 mx-auto mb-2 opacity-20" />
                <p className="text-sm">Select a submission to review</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
